import { useCallback, useMemo } from "react"

import type { RunRowLike } from "@/types/RunRow"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import {
  COMPARE_BY_OVERRIDE_PREFIX,
  getPaletteColor,
  METADATA_KEY_SEP,
} from "../constants"
import { COMPARE_BY_OPTION, type CompareByOption } from "../types"
import { TAG_UNTAGGED_KEY } from "../utils/compareBy"

interface RunWithCompareKeys {
  id?: string | null
  display_name?: string | null
  agent_id?: string | null
  persona_id_id?: string | null
  customer_metadata?: Record<string, unknown> | null
  tags?: string[] | null
}

export interface DistinctCompareByValue {
  overrideKey: string
  displayName: string
}

interface UseCompareByFeatureInput {
  compareBy: CompareByOption
  compareByMetadataKey: string | null
  multiRunMetadata: { runs?: (RunWithCompareKeys | null)[] } | null
  combinedRunRows: RunRowLike[] | null | undefined
  agentsById:
    | Record<string, { display_name?: string | null }>
    | null
    | undefined
  personasById:
    | Record<string, { display_name?: string | null }>
    | null
    | undefined
  compareByColorOverrides: Record<string, string>
  setCompareByColorOverrides: (value: Record<string, string>) => void
  /** When compareBy === "custom", supplies the active dimension so its groups can be enumerated for color/palette assignment. */
  activeCustomDimension?: {
    groups: Array<{ id: string; name: string }>
  } | null
  /** Map from tag name → the tag's own color. When compareBy === "tag" each
   *  group prefers its tag's color over the palette so the graphs match the
   *  colored chips the user picked. */
  tagColorMap?: Record<string, string | null> | null
}

interface UseCompareByFeatureResult {
  distinctCompareByValues: DistinctCompareByValue[]
  compareByValueToColor: Record<string, string>
  compareByColors: Record<string, string> | null
  setCompareByColor: (overrideKey: string, color: string) => void
}

export function useCompareByFeature(
  input: UseCompareByFeatureInput,
): UseCompareByFeatureResult {
  const {
    compareBy,
    compareByMetadataKey,
    multiRunMetadata,
    combinedRunRows,
    agentsById,
    personasById,
    compareByColorOverrides,
    setCompareByColorOverrides,
    activeCustomDimension = null,
    tagColorMap = null,
  } = input

  const distinctCompareByValues = useMemo((): DistinctCompareByValue[] => {
    if (compareBy === COMPARE_BY_OPTION.NONE) {
      return []
    }
    if (!multiRunMetadata?.runs && !combinedRunRows?.length) {
      return []
    }
    if (compareBy === "run" && multiRunMetadata?.runs) {
      const ids = [
        ...new Set(
          multiRunMetadata.runs
            .map((r) => r?.id)
            .filter((id): id is string => !!id),
        ),
      ]
      const runDisplayNames = new Map(
        multiRunMetadata.runs
          .filter((r): r is NonNullable<typeof r> => !!r?.id)
          .map((r) => [r.id!, r.display_name ?? r.id!]),
      )
      return ids.map((id) => ({
        overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.run}${id}`,
        displayName: runDisplayNames.get(id) ?? id,
      }))
    }
    if (compareBy === "agent") {
      const ids = multiRunMetadata?.runs
        ? [
            ...new Set(
              multiRunMetadata.runs
                .map((r) => r?.agent_id)
                .filter((id): id is string => !!id),
            ),
          ].sort()
        : combinedRunRows
          ? [
              ...new Set(
                combinedRunRows
                  .map((r) => r.agent_id)
                  .filter((id): id is string => !!id),
              ),
            ].sort()
          : []
      return ids.map((id) => ({
        overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.agent}${id}`,
        displayName: agentsById?.[id]?.display_name ?? id,
      }))
    }
    if (compareBy === "mutation") {
      if (!combinedRunRows || combinedRunRows.length === 0) {
        return []
      }
      const seen = new Map<string, string>()
      for (const row of combinedRunRows) {
        if (row.mutation_id && !seen.has(row.mutation_id)) {
          seen.set(row.mutation_id, row.mutation_name ?? row.mutation_id)
        }
      }
      return Array.from(seen.entries())
        .sort(([, a], [, b]) => a.localeCompare(b))
        .map(([id, name]) => ({
          overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.mutation}${id}`,
          displayName: name,
        }))
    }
    if (compareBy === "persona" && multiRunMetadata?.runs) {
      const ids = [
        ...new Set(
          multiRunMetadata.runs
            .map((r) => r?.persona_id_id)
            .filter((id): id is string => !!id),
        ),
      ].sort()
      return ids.map((id) => ({
        overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.persona}${id}`,
        displayName: personasById?.[id]?.display_name ?? id,
      }))
    }
    if (compareBy === "test_case") {
      if (!combinedRunRows || combinedRunRows.length === 0) {
        return []
      }
      const ids = [
        ...new Set(
          combinedRunRows
            .map((r) => r.test_set_row_id)
            .filter((id): id is string => !!id),
        ),
      ].sort()
      return ids.map((id) => ({
        overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.test_case}${id}`,
        displayName: id,
      }))
    }
    if (compareBy === "tag") {
      // Distinct tag names across every run in the report; each becomes
      // one palette slot. If any run has no tags we also emit the untagged
      // sentinel so the "Untagged" bucket gets its own color rather than
      // sharing whatever the default is.
      const values = new Set<string>()
      let hasUntagged = false
      if (multiRunMetadata?.runs) {
        for (const run of multiRunMetadata.runs) {
          const tags = run?.tags ?? []
          if (tags.length === 0) {
            hasUntagged = true
            continue
          }
          for (const tag of tags) {
            values.add(tag)
          }
        }
      }
      const sorted = Array.from(values).sort()
      const entries: DistinctCompareByValue[] = sorted.map((tag) => ({
        overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.tag}${tag}`,
        displayName: tag,
      }))
      if (hasUntagged) {
        entries.push({
          overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.tag}${TAG_UNTAGGED_KEY}`,
          displayName: "Untagged",
        })
      }
      return entries
    }
    if (compareBy === "custom" && activeCustomDimension) {
      return activeCustomDimension.groups.map((group) => ({
        overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.custom}${group.id}`,
        displayName: group.name,
      }))
    }
    if (compareBy === "metadata" && compareByMetadataKey) {
      const values = new Set<string>()
      // Pull from multiRunMetadata if available (simulation reports)
      if (multiRunMetadata?.runs) {
        for (const run of multiRunMetadata.runs) {
          const raw = run?.customer_metadata?.[compareByMetadataKey]
          const v =
            raw !== undefined && raw !== null ? stringifyUnknownValue(raw) : "—"
          values.add(v)
        }
      }
      // Also pull from combinedRunRows (monitoring reports, or as fallback)
      if (combinedRunRows) {
        for (const row of combinedRunRows) {
          const raw = row.customer_metadata?.[compareByMetadataKey]
          const v =
            raw !== undefined && raw !== null ? stringifyUnknownValue(raw) : "—"
          values.add(v)
        }
      }
      return Array.from(values)
        .sort()
        .map((v) => ({
          overrideKey: `${COMPARE_BY_OVERRIDE_PREFIX.metadata}${compareByMetadataKey}${METADATA_KEY_SEP}${v}`,
          displayName: v,
        }))
    }
    return []
  }, [
    compareBy,
    compareByMetadataKey,
    multiRunMetadata,
    combinedRunRows,
    agentsById,
    personasById,
    activeCustomDimension,
  ])

  const compareByValueToColor = useMemo(() => {
    const map: Record<string, string> = {}
    distinctCompareByValues.forEach(({ overrideKey, displayName }, i) => {
      // Precedence: user's manual override > per-tag stored color (only
      // meaningful for compareBy === "tag") > palette slot. The tag map
      // is keyed by display name, which for tags is the tag string; the
      // untagged sentinel has no entry there so it falls through to the
      // palette.
      const overrideColor = compareByColorOverrides[overrideKey]
      if (overrideColor) {
        map[overrideKey] = overrideColor
        return
      }
      if (compareBy === "tag") {
        const tagColor = tagColorMap?.[displayName]
        if (tagColor) {
          map[overrideKey] = tagColor
          return
        }
      }
      map[overrideKey] = getPaletteColor(i, distinctCompareByValues.length)
    })
    return map
  }, [compareBy, compareByColorOverrides, distinctCompareByValues, tagColorMap])

  const compareByColors = useMemo((): Record<string, string> | null => {
    if (compareBy === COMPARE_BY_OPTION.NONE) {
      return null
    }
    if (compareBy === "metadata" && !compareByMetadataKey) {
      return null
    }
    const out: Record<string, string> = {}
    distinctCompareByValues.forEach(({ overrideKey }) => {
      const color = compareByValueToColor[overrideKey]
      if (!color) {
        return
      }
      if (compareBy === "run") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.run.length)] = color
      } else if (compareBy === "mutation") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.mutation.length)] =
          color
      } else if (compareBy === "agent") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.agent.length)] = color
      } else if (compareBy === "persona") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.persona.length)] =
          color
      } else if (compareBy === "test_case") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.test_case.length)] =
          color
      } else if (compareBy === "custom") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.custom.length)] = color
      } else if (compareBy === "tag") {
        out[overrideKey.slice(COMPARE_BY_OVERRIDE_PREFIX.tag.length)] = color
      } else if (
        compareBy === "metadata" &&
        compareByMetadataKey &&
        overrideKey.includes(METADATA_KEY_SEP)
      ) {
        const valuePart = overrideKey.split(METADATA_KEY_SEP)[1]
        if (valuePart !== undefined) {
          out[valuePart] = color
        }
      }
    })
    return out
  }, [
    compareBy,
    compareByMetadataKey,
    distinctCompareByValues,
    compareByValueToColor,
  ])

  const setCompareByColor = useCallback(
    (overrideKey: string, color: string) => {
      setCompareByColorOverrides({
        ...compareByColorOverrides,
        [overrideKey]: color,
      })
    },
    [compareByColorOverrides, setCompareByColorOverrides],
  )

  return {
    distinctCompareByValues,
    compareByValueToColor,
    compareByColors,
    setCompareByColor,
  }
}
